import { Typography } from "@mui/material";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { useDataProvider, useRecordContext } from "react-admin";

export type DiagnosticInfo = {
  date: Date | null;
  iosVersion: string;
  watchosVersion: string;
  appVersion: string;
  mm4pdPermission: string;
  appBuild: string;
  pharmaMode: string;
};

type DiagnosticDataProvider = {
  getStreamIds: (params: unknown) => Promise<{
    data?: { id: string; maxTime: number }[];
  }>;
  getStream: (
    id: string,
    params: { start_time: string; end_time: string; format: string }
  ) => Promise<{
    json?: {
      cardinality: number;
      data: {
        event: {
          os_version: string;
          app_version: string;
          core_motion_authorization_status: string;
          app_build: string;
          pharma_mode?: string;
        }[];
        time: number[];
      };
    };
  }>;
};

export const getDiagnosticInfo = async (
  dataProvider: unknown,
  patientId: string | number
): Promise<DiagnosticInfo> => {
  const streamsDataProvider = dataProvider as DiagnosticDataProvider;
  const result = await streamsDataProvider.getStreamIds({
    meta: {
      filters: {
        patientId,
        algorithm: "ingest-rune-events.0",
        parameters: [{ key: "category", value: "device_info" }]
      }
    }
  });
  if (!result.data || result.data.length === 0) {
    throw new Error("No stream data available");
  }
  const stream = result.data.reduce((a, b) => (a.maxTime > b.maxTime ? a : b));
  const streamData = await streamsDataProvider.getStream(stream.id, {
    start_time: JSON.stringify(stream.maxTime - 10),
    end_time: JSON.stringify(stream.maxTime + 10),
    format: "json"
  });
  if (!streamData?.json?.data) {
    throw new Error("No stream data available");
  }
  const index = streamData.json.cardinality - 1;
  const record = streamData.json.data.event[index];
  return {
    date: new Date(streamData.json.data.time[index] * 1000),
    iosVersion: record.os_version,
    watchosVersion: "Not Set Yet",
    appVersion: record.app_version,
    mm4pdPermission: record.core_motion_authorization_status,
    appBuild: record.app_build,
    pharmaMode: record.pharma_mode || "unknown"
  };
};

// label and source, while not used in this component, are used by the StudyDatagridHeader to determine column display
// We disable the lint warning accordingly
/* eslint-disable @typescript-eslint/no-unused-vars */
export const DiagnosticField = (props: { label: string; source: string }) => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();

  const patientId = get(record, "id");

  const [diagnosticInfo, setDiagnosticInfo] = useState<DiagnosticInfo>({
    date: null,
    iosVersion: "",
    watchosVersion: "",
    appVersion: "",
    mm4pdPermission: "",
    appBuild: "",
    pharmaMode: ""
  });

  useEffect(() => {
    getDiagnosticInfo(dataProvider, patientId)
      .then(setDiagnosticInfo)
      .catch(() => {
        console.error("Error fetching diagnostic data:");
      });
  }, [dataProvider, patientId]);

  if (diagnosticInfo.date === null) {
    return (
      <Typography
        variant="body2"
        component="span"
        style={{ display: "flex", alignItems: "center" }}
      >
        No diagnostic data available
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      component="span"
      style={{ display: "flex", alignItems: "center" }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <div style={{ padding: "0 10px 0 0", flex: "0 1 auto" }}>
          iOS: <b>{diagnosticInfo.iosVersion}</b>
        </div>
        <div style={{ padding: "0 10px 0 0", flex: "0 1 auto" }}>
          / App: <b>{diagnosticInfo.appVersion}</b>
        </div>
        <div style={{ padding: "0 10px 0 0", flex: "0 1 auto" }}>
          / Mode: <b>{diagnosticInfo.pharmaMode}</b>
        </div>
        <div style={{ padding: "0 10px 0 0", flex: "0 1 auto" }}>
          <i>
            (
            {diagnosticInfo.date
              ? diagnosticInfo.date.toLocaleDateString()
              : ""}
            )
          </i>
        </div>
        {diagnosticInfo.mm4pdPermission !== "authorized" && (
          <div style={{ padding: "0 10px 0 0", flex: "0 1 auto" }}>
            / MM4PD Permission: <b>{diagnosticInfo.mm4pdPermission}</b>
          </div>
        )}
      </div>
    </Typography>
  );
};
